// GENSURF client generation framework (APP_GENSURF.md §2 C8 + C9, §3, §4):
// every generated look (the canopy pool, later the ground painter) is made
// ONCE per tile by a GenPass, into a pool, never per frame; the frame only
// samples. This module owns the parameters (C9), the queue and the pools'
// bookkeeping; the layers own their GL objects and their draw.
//
// PARAMETERS (C9). tunables.json gensurf = the live values, renderconfig.json
// gensurf = the ceilings, both per device class (device.js deviceClass;
// ?dev=<class> pins it). genResolve: by_class[cls] clamped to the ceiling at
// the same path (genClamp; a ceiling key ending _min is a floor), then the
// self-protection factors (adaptres.js govFactors: march steps, volume range,
// gen ms; the gen factor floored at 1 while sched hidden: genFactors) applied,
// + look / season / governor as they are. The tunables
// arrive with the welcome and live through net.onTunables (PUT /api/tunables
// -> gs IPC -> broadcast; no polling): applyGenTunables. A tunables object
// without the block (a game server forked before the block existed) leaves
// generation idle until the next save / Stop -> Start; an unknown version
// throws.
//
// HOT APPLY (HOT, a kind per leaf path, the by_class.<cls> prefix dropped —
// scripts/check-gensurf.mjs: every key has one, every one is a key):
//   uniform   a draw-time value: the layer moves its uniform, nothing regenerates
//   budget    the queue's ms a frame
//   need      re-ranks the queue (tiles enter / leave it), no tile regenerates
//   regen     a param-hash term: every tile of a pass that reads it re-queues,
//             nearest-first, the old content showing until the new one is done
//   pool      the pool resizes (the layer reallocates; its tiles regenerate)
//   governor  the frame-time ladder's config (adaptres.js setGovernor)
// Listeners (onGenChange) get the set of kinds that moved, with the live view.
//
// GenPass (C8) — a layer registers one per generated product:
//   id        short token ('canopy'); its run time is sec('gen.<id>')
//   params    the live paths it reads during run — each must be 'regen' or
//             'pool' (a look value in the hash would regenerate needlessly):
//             their values hash into every job key of the pass
//   pool      the pool id it renders into (gen.pool), or null
//   key(tile)          → the tile's identity string; the job key is key + '#'
//                        + the pass's param hash
//   need(tile, view)   → its RANK WITHIN THE PASS: lower runs first
//                        (nearest-first: distance in m); Infinity = not
//                        wanted (dropped, evictable). Never compared with
//                        another pass's ranks (their units differ: the
//                        ground's score 0..~11 vs the canopy's metres)
//   lead(tile)         optional: true = urgent (the ground's near loads):
//                        ahead of the fair share while its pass holds under
//                        LEAD_SHARE of the gen ms; 2 = a GAP (a visible
//                        coarse tile: the ground's vt.urgent): in play every
//                        other pass's plain lead waits (holes keep theirs)
//                        while it leads; while sched hidden (the
//                        beam-in overlay: the latch) every tile of a pass
//                        declaring it leads, unbounded
//   inputs(tile)       → asset urls (or []), fetched through assetq fetchAsset
//                        ('bulk', scored by the installed need resolver)
//                        before the first run; a failed / dropped fetch
//                        retries after RETRY_MS; [] = runnable at once, never
//                        waiting for one of the IN_FLIGHT fetch slots. Asked
//                        again when its fetch starts (what the pass decoded
//                        since is not asked); a url already in flight or held
//                        by another queued tile is SHARED (_raw: one fetch per
//                        url while any queued tile holds it). gen.reask(id,
//                        tileId): the pass dropped a tile's inputs (asks again)
//   ready(tile)        optional: false = NOT READY (its ground is not resident yet): not a failure —
//                        the tile is skipped by the run pick (inputs still fetch), no warn, no
//                        stats.failed, no RETRY_MS backoff, no slot; asked at most once a frame, and
//                        picked on the first scan after it turns true. One waiting past
//                        READY_SLOW_AFTER_MS (ground that never comes) is asked once a READY_SLOW_MS
//   run(gl, job, ms)   → 'more' | 'yield' | 'done' | a Promise ('yield' = more, but in
//                        the NEXT frame: the queue ends this frame, the job
//                        stays current — a GPU stage a frame; a Promise = THE PARKED
//                        WAIT, below); job = {tile, id, key, inputs,
//                        target: {pool, layer}, state: {}} — the layer slot
//                        it renders into, ms the frame's budget left;
//                        deterministic per tile (world-coordinate hash, tile
//                        halo) so neighbours generated apart meet seamlessly
//   fill(tile)         optional: true = the tile shows nothing yet (a hole, not
//                        a refinement): in play its pass joins the leading
//                        passes (the fair share among them), never while hidden
//   step(job)          optional: a label of the job's next run ('' = the pass's
//                        first): the learned floor is kept per pass + label
//   minMs()            optional: the smallest run it can make (ms, <= MIN_RUN_MS): a
//                        frame's ms below it is banked until the credit holds it
//   prep(tile, bufs)   optional: → a Promise of the tile's decoded inputs (job.inputs), run off the
//                        frame on its fetched bufs (an async decode / hash never holds the queue
//                        with a 'yield' while its promise is pending: that ate the frame's gen ms
//                        of every other pass); at most PREP_AHEAD tiles of a pass hold prepped
//                        inputs not yet run (decoded inputs are big: the rest wait as fetched
//                        bytes, best-ranked first); a failure is a failed fetch (below)
//   fail(tile, err)    optional: the tile failed DETERMINISTICALLY (below) — the layer draws its
//                        fallback; called once per tile
//   init(gl)           optional: the pass's ONE-TIME GL setup (programs issued, storage), idempotent —
//                        THE PASS INIT (xr-stalls: the canopy's / shrub's / ring's first run carried
//                        it, 96-191 ms in play on the Quest, session 870): called once per pass while
//                        sched is HIDDEN (the beam-in overlay / the VR black), one pass a frame, before
//                        the frame's budget; a pass registered in play inits at its first run, as before
//   ref(tile, inputs)  → typed arrays: the CPU reference (gates only)
//   done(job) / evict(id, layer)  optional: a slot turned resident / freed
// FAILURES (a fetch, a prep or a run that throws): an error marked `permanent` (err.permanent:
// a bad class byte, a wrong input size, a hash mismatch, a missing GL capability — the same
// bytes / device fail the same way) is DETERMINISTIC: the tile is dead for the queue's life (one
// page = one bake; R.dead, never forgotten by want()), console.error once, pass.fail(tile, err),
// never retried. Anything else is TRANSIENT (the network, a dropped fetch): retried after
// RETRY_MS x 2^(n - 1) for its n-th failure in a row, at most RETRY_MAX_MS (a done tile resets n).
// The layer says which tiles it wants (gen.want(passId, tiles, view), at its
// own scan cadence); gen.slotOf(passId, id) → the resident layer (-1 none).
//
// THE QUEUE (genFrame, once a tick): nothing before sched heavyOk (the quiet
// start: Map first); ms = min(live gen.ms[genPhase: sched phase, 'beam' while hidden], sched budget()) — the
// XR cap included; a frame's run time past ms is a DEBT the next frames pay
// off by running nothing (GmTex._copy's rule), at least one run a frame not
// paying. The current job first, then a stale tile whose inputs are in (at
// most IN_FLIGHT fetches): the PASS first — a leading tile's pass (lead; in play
// only while it holds under LEAD_SHARE of the gen ms), else
// the pass with runnable work that used the least gen ms (decayed, half-life
// SHARE_HALF_MS: the fair share, one per sched mode: hidden / shown) — then that pass's best-ranked tile; a fetch
// picks the pass with the fewest fetches in flight, then the same order. A
// pass whose ranks always beat another's starved it (gs-forestvol, live
// fb0cc40e: the ground's never-empty list, 0 canopy runs in 9 min — the far
// volume drew nothing). A job renders into a free slot, else
// the worst-ranked resident tile's (only one ranked worse than the job), else
// its own stale slot; on 'done' its old slot frees.
// THE PARKED WAIT (w57 2026-11-01: a canopy job whose SHD2 decodes pended returned 'yield' — it held the one
// current job and its pass was charged each frame's rest with nothing done; A/B x2 desktop-igpu Map entries:
// the canopy's share ~5 s after the latch 166 -> 9-14 ms, release / control no worse; the painter's idle span
// after the latch is not this, see LEAD_SHARE): a run that returns a Promise waits on async input, not CPU — the job PARKS (its slot
// stays reserved, its state kept) and the frame goes on with the next pick; its pass starts no other job
// while it is parked, and once the Promise settles the parked job is its pass's one candidate, picked in
// the pass order as any (no starvation) and resumed where it stopped. The same jobs and outputs; only
// the order in time moves. A 'yield' (a CPU slice spent, a GPU stage a frame) keeps the job current.
// InputCache: a pass's DECODED inputs, an LRU whose eviction skips the urls its
// queued tiles still need (pin, at the layer's want): with _raw, a still player
// re-asks nothing and a filling one fetches each input once per need.
import RENDER_CFG from './renderconfig.json' with { type: 'json' };
import { fetchAsset } from './assetq.js';
import { sec, phase, budget, heavyOk, hidden } from './sched.js';
import { deviceClass, DEVICE_CLASSES } from './device.js';
import { setGovernor, govFactors, onGovernor } from './adaptres.js';

export const GENSURF_VER = 1;
export const MB = 1e6;
export const IN_FLIGHT = 4, RETRY_MS = 5000, RETRY_MAX_MS = 60000, PREP_AHEAD = 2;
// the backoff of a tile's n-th transient failure in a row (n >= 1)
export const retryMs = (n) => Math.min(RETRY_MAX_MS, RETRY_MS * 2 ** Math.max(0, n - 1));
export const READY_SLOW_AFTER_MS = 10000, READY_SLOW_MS = 1000;
// a pass's declared indivisible run (pass.minMs(): its fixed per-run cost + its next step) is held
// to this: a frame whose ms is below the floor BANKS it (credit), the pass runs once the credit holds
// the floor, with the whole credit as its ms — no run is handed a slice it cannot fit (phone-lo's
// governor x 0.25: 0.38 ms slices, ~400 runs over by ~0.1 ms each)
export const MIN_RUN_MS = 1.5;
// ... and to its MEASURED indivisible run (gs-gpupainter-pace item 3, orchestrator ruling (a)): a run past
// the ms it was handed (it could not stop sooner) raises its pass's floor to that run's ms, at most
// LEARN_MAX_MS — so the first over-run makes debt once, the next runs are banked to their size; the
// learned part halves every LEARN_HALF_MS without a new over-run (a passing stall is forgotten).
// gpupainter-p8 phone-lo: the canopy pass's 5.6 ms runs on 1.5 ms slices left 4.1 ms debt at the release
export const LEARN_MAX_MS = 8, LEARN_HALF_MS = 10e3;
// a pass's main-thread work OUTSIDE its runs (groundgen's want scan) charged to the gen budget
// (charge(ms)): the next frames' slices are that much smaller; at most this much pending
export const CHARGE_MAX_MS = 10;
// the fair share's memory: a pass's gen ms used halves every SHARE_HALF_MS (longer than one sticky
// ground job's wall time, ~6.5 s on desktop-igpu; shorter would let a pass forget a run it just had)
export const SHARE_HALF_MS = 8e3;
// in play a leading pass leads while its share of the gen ms used is below this (the ground's near
// loads never drained on the live world: an unbounded lead was the starvation again); hidden: always
export const LEAD_SHARE = 0.75;
// the load report's gen block lists at most this many passes (report())
export const GEN_REPORT_MAX = 8;

// C3 canopy tile layout: A RGBA8 + B RGBA8 + H R16UI, n² texels, full mip
// chains (APP_FORESTVOL §3) — bytes per pool layer (256: 873,810)
export const CANOPY_TEXELS = 256;
export function canopyLayerBytes(n = CANOPY_TEXELS) {
  let t = 0;
  for (let s = n; s >= 1; s >>= 1) t += s * s;
  return t * (4 + 4 + 2);
}

export const HOT = {
  'canopy.layers': 'pool', 'canopy.level_min': 'need', 'canopy.level_max': 'need',
  // the shrub family (canopygen.js FAM.shrub): fade_m moves the inner cut
  // N − F of the WANTED set, so it re-ranks like range_m (need), not a uniform
  'shrub.layers': 'pool', 'shrub.range_m': 'need', 'shrub.fade_m': 'need',
  'vol.range_m': 'uniform', 'vol.steps': 'uniform', 'vol.steps_optic': 'uniform', 'vol.splat_m': 'uniform',
  'shell.r8_m': 'uniform', 'shell.r16_m': 'uniform', 'shell.r32_m': 'uniform', 'shell.r64_m': 'uniform',
  'sprites.view_m': 'uniform', 'sprites.fade_m': 'uniform',
  'gen.ms.play': 'budget', 'gen.ms.beam': 'budget', 'gen.ms.map': 'budget',
  'ground.finest_level': 'regen', 'ground.tiling': 'regen', 'ground.strata': 'regen',
  'grass.radius_m': 'uniform',
  'grass.tufts': 'uniform', 'grass.bushes': 'uniform', 'grass.stones': 'uniform', 'grass.flowers': 'uniform',
  'look.canopy.gain': 'uniform', 'look.canopy.sat': 'uniform',
  'look.ground.gain': 'uniform', 'look.ground.sat': 'uniform', 'look.snow.gain': 'uniform',
  'season.date': 'regen', 'season.winter_doy': 'uniform', 'season.winter_drop_m': 'uniform', 'season.aspect_k_m': 'uniform',
  'season.shed_deg_lo': 'uniform', 'season.shed_deg_hi': 'uniform', 'season.catch_gain': 'uniform', 'season.wet_gain': 'uniform',
  'governor.enabled': 'governor', 'governor.flat': 'governor', 'governor.flat_target_fps': 'governor',
  'governor.window_ms': 'governor', 'governor.slow': 'governor', 'governor.miss_frac': 'governor',
  'governor.ok_frac': 'governor', 'governor.cpu_frac': 'governor', 'governor.hitch_ms': 'governor',
  'governor.up_ms': 'governor', 'governor.up_max_ms': 'governor',
  'governor.steps.notches': 'governor', 'governor.steps.min_k': 'governor',
  'governor.vol.notches': 'governor', 'governor.vol.min_k': 'governor',
  'governor.gen.notches': 'governor', 'governor.gen.min_k': 'governor',
};
// the live view's own keys (not tunables): the class
const HOT_SELF = { cls: 'pool' };

// leaf paths of a plain object → [[path, value]] (keys starting '_' skipped)
export function leaves(o, pre = '', out = []) {
  for (const [k, v] of Object.entries(o || {})) {
    if (k.startsWith('_')) continue;
    const p = pre ? `${pre}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) leaves(v, p, out);
    else out.push([p, v]);
  }
  return out;
}
const at = (o, p) => p.split('.').reduce((a, k) => (a == null ? undefined : a[k]), o);
function put(o, p, v) {
  const ks = p.split('.');
  let a = o;
  for (const k of ks.slice(0, -1)) a = a[k] ??= {};
  a[ks[ks.length - 1]] = v;
}
const clone = (o) => JSON.parse(JSON.stringify(o));

// one class's live values clamped to its ceilings (a copy)
export function genClamp(vals, ceil) {
  const out = clone(vals || {});
  for (const [p, c] of leaves(ceil)) {
    if (typeof c !== 'number') continue;
    const lo = p.endsWith('_min'), tp = lo ? p.slice(0, -4) : p;
    const v = at(out, tp);
    if (typeof v !== 'number') continue;
    put(out, tp, lo ? Math.max(v, c) : Math.min(v, c));
  }
  return out;
}

// the governor's factors as the live view applies them (gs-gpupainter p6 ruling (b)(ii)): while the
// world is HIDDEN (sched hidden: the beam-in overlay up, no frame of it shown — the frame time the
// governor protects is nobody's) the gen factor floors at 1 (phone-lo's governor at x 0.25 left the
// beam-in latch 8-10 slots in 300 s: gpupainter-p5); from the overlay's drop on, the governor's own
export function genFactors(f, hid) { return hid && !(f.gen >= 1) ? { ...f, gen: 1 } : f; }
// the gen.ms row a frame reads (beam-order, probe 2026-10-24: at the latch phase 'play' cut the painter to
// play's 1-1.5 ms a frame behind the overlay, igpu's top-level tiles 11-17 s each): while HIDDEN the 'beam'
// row from the Beam to the overlay's drop, the latch included; else the sched phase's
export const genPhase = (ph, hid) => (hid && ph === 'play' ? 'beam' : ph);

// the live view for class cls: clamp, then the governor's factors (f)
export function genResolve(g, cls, ceilings = RENDER_CFG.gensurf, f = { steps: 1, vol: 1, gen: 1 }) {
  if (!g || !cls) return null;
  if (g.version !== GENSURF_VER) throw new Error(`tunables gensurf version ${g.version} (client reads ${GENSURF_VER})`);
  if (!ceilings || ceilings.version !== GENSURF_VER) throw new Error('renderconfig gensurf: missing or unknown version');
  const bc = g.by_class && g.by_class[cls], cc = ceilings.by_class && ceilings.by_class[cls];
  if (!bc || !cc) throw new Error(`gensurf: no block for device class ${cls}`);
  const v = genClamp(bc, cc);
  if (v.vol) {
    v.vol.steps = Math.round(v.vol.steps * f.steps);
    v.vol.steps_optic = Math.round(v.vol.steps_optic * f.steps);
    v.vol.range_m = Math.round(v.vol.range_m * f.vol);
  }
  if (v.gen && v.gen.ms) for (const k of Object.keys(v.gen.ms)) v.gen.ms[k] = Math.round(v.gen.ms[k] * f.gen * 100) / 100;
  return { cls, ...v, look: clone(g.look || {}), season: clone(g.season || {}), governor: clone(g.governor || {}) };
}

// the kinds that moved between two live views (null = nothing before: all)
export function genDiff(a, b) {
  const kinds = new Set();
  if (!b) return kinds;
  const pa = new Map(a ? leaves(a) : []), pb = leaves(b);
  for (const [p, v] of pb) {
    if (a && pa.get(p) === v) continue;
    let k = HOT_SELF[p] || HOT[p];
    // a key the table misses (check-gensurf keeps it complete): the safe kind
    if (!k) { console.warn(`gensurf: no hot-apply kind for ${p} (regenerates)`); k = 'regen'; }
    kinds.add(k);
  }
  return kinds;
}

// a small stable hash (FNV-1a 32) of a string → 8 hex
export function hash8(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16).padStart(8, '0');
}

const PASS_FN = ['key', 'need', 'inputs', 'run'];

export class GenQueue {
  // env (scripts/check-gensurf.mjs): { now, heavyOk, phase, budget, fetch, sec }
  constructor(env = {}) {
    this.env = {
      now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
      heavyOk, phase, budget, sec, hidden, fetch: (url, o) => fetchAsset(url, o), ...env,
    };
    this.passes = new Map();      // id → {pass, hash, tiles: Map(id → entry)}
    this._uninit = [];            // passes whose init(gl) is still owed (THE PASS INIT: while hidden)
    this.pools = new Map();       // id → {layers, bytes, slots: [entry | null]}
    this.live = null;
    this._cur = null;             // the job in progress {P, e, job}
    this._parked = new Map();     // pass R -> {c (its job, as _cur held it), ready (its Promise settled)}: THE PARKED WAIT
    this._debt = 0;
    this._bank = 0;               // the frames' ms banked below a pass's minMs (credit)
    this._fetching = 0;
    this._raw = new Map();        // url → {p: its one fetch, n: queued tiles holding it} (shared inputs)
    this.shareInputs = true;      // (the gate's red twin: false, every tile fetches its own)
    this._chg = 0;                // the charged ms not yet taken off a slice (charge)
    this.stats = { runs: 0, done: 0, requeued: 0, evicted: 0, failed: 0, dead: 0, debt_ms: 0, banked: 0, charged_ms: 0, fetched: 0, joined: 0 };
    this.permanent = true;        // (the gate's red twin: false, a deterministic failure is retried like any other)
    this.overBy = {};             // pass id -> its runs past the ms they were handed {n, ms (summed over), max, run_max, at}
    this.tally = new Map();       // pass id -> {runs, fail, ffail, ms} since page start (report(): the load report's gen block)
    this.learn = new Map();       // pass id (+ '.' + step label) -> its measured indivisible run {ms, at} (_floor; LEARN_MAX_MS)
    this.learnMax = LEARN_MAX_MS; // (the gate's red twin: 0, no learned floor)
    this.debtOf = null;           // the pass whose run over made the debt being paid (the probe's ground / canopy split)
    this.used = { hidden: new Map(), shown: new Map() };   // per sched mode: pass id -> its gen ms used {ms, at} (the fair share, _share)
    this.shareModes = true;       // (the gate's red twin: false, one share over both modes)
    this.fair = true;             // (the gate's red twin: false, one rank order over every pass)
    this.leadShare = LEAD_SHARE;  // (the gate's red twin: Infinity, an unbounded lead)
    this.fills = true;            // (the gate's red twin: false, a hole waits behind the lead like a refinement)
    this.gapLeads = true;         // (the gate's red twin: false, a gap lead shares the lead with every plain one)
    this.gapHidden = true;        // (the gate's red twin: false, while hidden a gap is a plain lead — the passes by share)
    this.yieldShare = true;       // (the gate's red twin: false, a yielded frame costs its pass only its CPU ms)
    this.stepFloor = true;        // (the gate's red twin: false, one learned floor for every run of a pass)
    this.readyGate = true;        // (the gate's red twin: false, a run over absent ground throws and backs off RETRY_MS)
    this.parkWait = true;         // (the gate's red twin: false, a run's Promise is a 'yield': the waiting job stays current)
    this._tick = 0;               // the frame count (a tile's ready() is asked once a frame)
  }

  // a pass's gen ms used now (halving every SHARE_HALF_MS) / spend ms of it — in the sched mode's own
  // share (SHARE-MODES): the beam-in's spend (the ground alone: holes never lead hidden) never weighs on
  // the shown order (probe 2026-10-31 desktop-igpu: ground 1035 vs canopy 134 / shrub 138 at overlay-off,
  // the forest's holes took every frame, 0 ground runs 14 s with 9 slots listed — the player's first view)
  _used() { return this.used[this.shareModes && !(this.env.hidden && this.env.hidden()) ? 'shown' : 'hidden']; }
  _share(id, U = this._used()) {
    const u = U.get(id);
    return u ? u.ms * 2 ** (-(this.env.now() - u.at) / SHARE_HALF_MS) : 0;
  }
  _spend(id, ms) { const U = this._used(); U.set(id, { ms: this._share(id, U) + ms, at: this.env.now() }); }
  // a pass's cumulative counters (read-only diagnostics: nothing in the queue reads them)
  _tal(id) {
    let t = this.tally.get(id);
    if (!t) this.tally.set(id, t = { runs: 0, fail: 0, ffail: 0, ms: 0 });
    return t;
  }

  // ms of a pass's own main-thread work outside its runs (its scan): INSIDE the gen budget — taken
  // off the next frames' slices (the debt stays the runs' own over-run)
  charge(ms) {
    if (!(ms > 0)) return;
    this._chg = Math.min(CHARGE_MAX_MS, this._chg + ms);
    this.stats.charged_ms += ms;
  }

  // a pool: `layers` slots of `bytes` each (the layer allocates the GL side)
  pool(id, layers, bytes) {
    const old = this.pools.get(id);
    const P = { id, layers: Math.max(0, layers | 0), bytes, slots: new Array(Math.max(0, layers | 0)).fill(null) };
    this.pools.set(id, P);
    if (old) {
      // a resize: every resident tile of the pool regenerates into the new one
      for (const e of old.slots) if (e) { e.slot = -1; e.done = null; }
      if (this._cur && this._cur.job.target.pool === id) this._cur = null;
      for (const [R, K] of this._parked) if (K.c.job.target.pool === id) this._parked.delete(R);
    }
    return P;
  }
  poolMB() { let b = 0; for (const P of this.pools.values()) b += P.layers * P.bytes; return b / MB; }

  register(pass) {
    for (const f of PASS_FN) if (typeof pass[f] !== 'function') throw new Error(`GenPass ${pass.id}: ${f}() missing`);
    if (!/^[a-z0-9_]{1,24}$/.test(pass.id || '')) throw new Error(`GenPass id ${pass.id}`);
    if (this.passes.has(pass.id)) throw new Error(`GenPass ${pass.id} registered twice`);
    for (const p of pass.params || []) {
      const k = HOT[p];
      if (k !== 'regen' && k !== 'pool') throw new Error(`GenPass ${pass.id}: param ${p} is '${k}', not a generation value`);
    }
    if (pass.pool && !this.pools.has(pass.pool)) throw new Error(`GenPass ${pass.id}: pool ${pass.pool} unknown`);
    // dead: tile id -> the deterministic failure's message (FAILURES: never retried, never forgotten)
    const R = { pass, hash: this._hash(pass), tiles: new Map(), fetching: 0, dead: new Map() };
    this.passes.set(pass.id, R);
    if (typeof pass.init === 'function') this._uninit.push(R);
    return R;
  }

  _hash(pass) {
    if (!this.live) return '';
    return hash8(JSON.stringify((pass.params || []).map((p) => at(this.live, p))));
  }

  // a new live view: re-hash every pass (a moved regen term re-queues its
  // tiles: `done` no longer equals the job key, the slot stays readable)
  setLive(live) {
    this.live = live;
    for (const R of this.passes.values()) {
      const h = this._hash(R.pass);
      if (h === R.hash) continue;
      R.hash = h;
      for (const e of R.tiles.values()) if (e.done) this.stats.requeued++;
      if (this._cur && this._cur.R === R) this._drop(this._cur);
      this._unpark(R);
    }
  }

  // the tiles a layer wants now (any it does not list: not wanted)
  want(passId, tiles, view) {
    const R = this.passes.get(passId);
    if (!R) throw new Error(`gen.want: no pass ${passId}`);
    const seen = new Set();
    for (const t of tiles) {
      const id = R.pass.key(t);
      seen.add(id);
      let e = R.tiles.get(id);
      if (!e) R.tiles.set(id, e = { R, id, tile: t, rank: Infinity, slot: -1, done: null, inputs: null, fetching: false, retryAt: 0 });
      if (e.tile !== t) e.urls = undefined;
      e.tile = t;
      e.rank = R.pass.need(t, view);
    }
    for (const e of R.tiles.values()) {
      if (seen.has(e.id)) continue;
      e.rank = Infinity;
      if (e.slot < 0 && !e.fetching && !e.prepping) { this._unraw(e); R.tiles.delete(e.id); }   // nothing held: forget it
    }
  }

  jobKey(R, e) { return `${e.id}#${R.hash}`; }
  slotOf(passId, id) { const R = this.passes.get(passId), e = R && R.tiles.get(id); return e ? e.slot : -1; }
  // a tile shows its newest content (stale: an older param hash) — 'none' | 'stale' | 'fresh'
  stateOf(passId, id) {
    const R = this.passes.get(passId), e = R && R.tiles.get(id);
    if (!e || e.slot < 0) return 'none';
    return e.done === this.jobKey(R, e) ? 'fresh' : 'stale';
  }

  // pass.ready(tile) for entry e: once a frame; a tile waiting past READY_SLOW_AFTER_MS once a READY_SLOW_MS
  _ready(R, e) {
    if (!this.readyGate || !R.pass.ready) return true;
    const now = this.env.now(), r = e.rdy;
    if (r && r.tick === this._tick) return r.v;
    if (r && !r.v && now - r.since > READY_SLOW_AFTER_MS && now - r.at < READY_SLOW_MS) return false;
    const v = !!R.pass.ready(e.tile);
    e.rdy = { v, tick: this._tick, at: now, since: !v && r && !r.v ? r.since : now };
    return v;
  }

  // the stale tiles to try, best first (inputs: in → runnable, else to fetch): per pass its best
  // leading tile, else its best hole (fill), else its best-ranked one (ranks compare only WITHIN a
  // pass); the passes ordered lead first (a lead under LEAD_SHARE, or a hole in play: the fair share
  // among them), then (fetch) the fewest fetches in flight, then the least gen ms used (_share)
  _picks(runnable) {
    const now = this.env.now(), hid = !!(this.env.hidden && this.env.hidden());
    const out = [];
    for (const R of this.passes.values()) {
      let best = null, lead = null, hole = null, gap = false;   // (this pass's best-ranked tile, its best leading one, its best hole, a gap leads)
      const L = this.fair && R.pass.lead, F = this.fair && this.fills && !hid && R.pass.fill;
      // a parked job (THE PARKED WAIT): its pass's one runnable candidate, once its wait settled
      const K = runnable ? this._parked.get(R) : null;
      for (const e of R.tiles.values()) {
        if (!(e.rank < Infinity) || e.done === this.jobKey(R, e) || e.retryAt > now || R.dead.has(e.id)) continue;
        if (this._cur && this._cur.e === e) continue;
        if (K && (!K.ready || K.c.e !== e)) continue;
        if (runnable ? !e.inputs : e.inputs || e.bufs || e.fetching || e.prepping) continue;
        if (runnable && !K && !this._ready(R, e)) continue;
        if (!best || e.rank < best.rank) best = e;
        if (L) { const lp = R.pass.lead(e.tile), lv = lp === 2 && (!hid || this.gapHidden) ? 2 : hid || lp; if (lv === 2) gap = true; if (lv && (!lead || e.rank < lead.rank)) lead = e; }
        if (F && (!hole || e.rank < hole.rank) && R.pass.fill(e.tile)) hole = e;
      }
      if (best) out.push({ R, best, e: lead || hole || best, lead: !!lead, gap, hole, share: this._share(R.pass.id) });
    }
    if (!this.fair) return out.sort((a, b) => a.e.rank - b.e.rank);
    const tot = out.reduce((a, p) => a + p.share, 0);
    for (const p of out) if (p.lead && (!hid || p.gap) && tot > 0 && p.share >= this.leadShare * tot) { p.lead = false; p.e = p.best; }
    // a GAP lead (lead() === 2: the ground's on-screen tiles 2+ levels coarse, the tile under the eye) takes the lead
    // from every plain one (the far ring's tier 2: quick-painted, it only refines) while it holds under LEAD_SHARE —
    // hidden too (THE HIDDEN GAP, pp5 D; w57 probe 2026-10-31, bake 0ec8fb57, igpu Map entry: from the latch to the
    // overlay's release every tile of every pass led, the passes by share, so the ring took 2.6 s + 3 more gaps with the
    // landing's floor key ready and listed — the overlay waited on it). A gap exists only in play (gmtex urgent: viewOn),
    // so the beam's blanket lead is unchanged
    if (this.gapLeads && out.some((p) => p.lead && p.gap)) for (const p of out) if (p.lead && !p.gap) { p.lead = false; p.e = p.best; }
    // a pass with a hole leads in play (the far forest's missing tiles beside the ground's near loads: a
    // hole is a visible gap, a refinement is not — live fb0cc40e: 25 of 78 canopy tiles 266 s after the latch)
    for (const p of out) if (p.hole && !p.lead) { p.lead = true; p.e = p.hole; }
    return out.sort((a, b) => (b.lead - a.lead) || (runnable ? 0 : a.R.fetching - b.R.fetching) || (a.share - b.share));
  }

  // the tile's input urls, once per tile object (want() hands a new one -> asked again)
  _urls(R, e) {
    if (e.urls === undefined) e.urls = R.pass.inputs(e.tile) || [];
    return e.urls;
  }

  // the shared fetch of one input url: joined while in flight or held by a queued tile
  _rawFetch(u) {
    let r = this.shareInputs ? this._raw.get(u) : null;
    if (r) this.stats.joined++;
    else {
      r = { n: 0, p: Promise.resolve(this.env.fetch(u, { prio: 'bulk' })).then((b) => { if (b == null) throw new Error('dropped'); return b; }) };
      this.stats.fetched++;
      if (this.shareInputs) {
        this._raw.set(u, r);
        r.p.catch(() => { if (this._raw.get(u) === r) this._raw.delete(u); });
      }
    }
    r.n++;
    return r.p;
  }
  // an entry lets its inputs go: a url no queued tile holds any more leaves _raw
  _unraw(e) {
    if (!e.raw) return;
    for (const u of e.raw) { const r = this._raw.get(u); if (r && --r.n <= 0) this._raw.delete(u); }
    e.raw = null;
  }
  // the pass dropped a tile's inputs (they no longer match its ask): fetched again (asked anew) at the retry
  reask(passId, id) {
    const R = this.passes.get(passId), e = R && R.tiles.get(id);
    if (!e) return;
    this._unraw(e);
    e.inputs = null; e.bufs = null; e.urls = undefined;
  }

  _fetch() {
    // an entry with no inputs is runnable at once: it takes no fetch slot (all IN_FLIGHT may be
    // held by another pass's fetches — the canopy's 'bulk' ones are paused from the Beam to the
    // latch, which starved the input-less ground pass: gs-gpupainter)
    for (const R of this.passes.values()) {
      for (const e of R.tiles.values()) {
        if (e.inputs || e.bufs || e.fetching || e.prepping || !(e.rank < Infinity) || e.done === this.jobKey(R, e) || R.dead.has(e.id)) continue;
        if (!this._urls(R, e).length && !R.pass.prep) e.inputs = [];
      }
    }
    while (this._fetching < IN_FLIGHT) {
      const b = this._picks(false)[0];
      if (!b) return;
      const { R, e } = b;
      // asked again now (what the pass decoded since its first ask is not fetched)
      const urls = (e.urls = R.pass.inputs(e.tile) || []);
      if (!urls.length && !R.pass.prep) { e.inputs = []; continue; }
      e.fetching = true; this._fetching++; R.fetching++;
      this._unraw(e);
      e.raw = urls.slice();
      Promise.all(urls.map((u) => this._rawFetch(u)))
        .then((bufs) => { if (R.pass.prep) e.bufs = bufs; else e.inputs = bufs; })
        .catch((err) => { this._unraw(e); this._failed(R, e, err, 'ffail'); })
        .finally(() => { e.fetching = false; this._fetching--; R.fetching--; });
    }
  }
  // pass.prep over the fetched bufs: per pass at most PREP_AHEAD tiles prepped (or prepping) and not
  // yet run, the best-ranked first
  _prep() {
    const now = this.env.now();
    for (const R of this.passes.values()) {
      if (!R.pass.prep) continue;
      let held = 0, best = null;
      for (const e of R.tiles.values()) {
        if (e.prepping || (e.inputs && e.rank < Infinity && e.done !== this.jobKey(R, e))) { held++; continue; }
        if (!e.bufs || !(e.rank < Infinity) || e.retryAt > now || R.dead.has(e.id)) continue;
        if (!best || e.rank < best.rank) best = e;
      }
      if (!best || held >= PREP_AHEAD) continue;
      const e = best, bufs = e.bufs;
      e.bufs = null; e.prepping = true;
      Promise.resolve().then(() => R.pass.prep(e.tile, bufs))
        .then((v) => { e.inputs = v; })
        .catch((err) => { this._unraw(e); this._failed(R, e, err, 'fail'); })
        .finally(() => { e.prepping = false; });
    }
  }

  // a tile's fetch / prep / run threw (FAILURES): permanent -> dead + pass.fail, else the backoff;
  // kind = its tally counter ('ffail' an input fetch, 'fail' a prep / run: report()) — a permanent
  // failure counts once (it is never tried again)
  _failed(R, e, err, kind) {
    const dead = this.permanent && err && err.permanent;
    if (dead && R.dead.has(e.id)) return;
    this.stats.failed++;
    this._tal(R.pass.id)[kind]++;
    if (dead) {
      const msg = (err && err.message) || String(err);
      R.dead.set(e.id, msg);
      this.stats.dead++;
      console.error(`gen.${R.pass.id} ${e.id}: ${msg} — deterministic, not retried`);
      if (R.pass.fail) { try { R.pass.fail(e.tile, err); } catch (f) { console.error(`gen.${R.pass.id} ${e.id} fail():`, f && f.message); } }
      return;
    }
    e.fails = (e.fails || 0) + 1;
    e.retryAt = this.env.now() + retryMs(e.fails);
  }

  // a slot for entry e of pass R (rank r): free > worst-ranked resident ranked
  // worse than e > e's own; -1 none (the pool holds only better tiles)
  _slot(R, e) {
    const P = R.pass.pool && this.pools.get(R.pass.pool);
    if (!P) return -1;
    let worst = -1, wr = e.rank;
    for (let i = 0; i < P.slots.length; i++) {
      const o = P.slots[i];
      if (!o) return i;
      if (o === e || (this._cur && this._cur.e === o) || (this._parked.size && this._parkedE(o))) continue;
      if (o.rank > wr) { wr = o.rank; worst = i; }
    }
    if (worst >= 0) return worst;
    return e.slot;
  }

  _start(R, e) {
    let layer = -1;
    if (R.pass.pool) {
      layer = this._slot(R, e);
      if (layer < 0) return null;
      const P = this.pools.get(R.pass.pool), o = P.slots[layer];
      if (o && o !== e) {
        // evicted: its owner forgets the slot
        o.slot = -1; o.done = null; this.stats.evicted++;
        if (o.R.pass.evict) o.R.pass.evict(o.id, layer);
      }
      P.slots[layer] = e;   // reserved (its old slot, if any, frees at 'done')
    }
    return { R, e, prev: e.slot, job: { tile: e.tile, id: e.id, key: this.jobKey(R, e), inputs: e.inputs,
      target: { pool: R.pass.pool || null, layer }, state: {} } };
  }

  _finish(c) {
    const { R, e, prev, job } = c;
    const P = R.pass.pool && this.pools.get(R.pass.pool);
    if (P && prev >= 0 && prev !== job.target.layer && P.slots[prev] === e) {
      P.slots[prev] = null;
      if (R.pass.evict) R.pass.evict(e.id, prev);
    }
    e.slot = job.target.layer;
    e.done = job.key;
    e.inputs = null; e.bufs = null;
    e.fails = 0;
    this._unraw(e);
    this.stats.done++;
    if (R.pass.done) R.pass.done(job);
  }

  // once a tick (genFrame): gl the renderer's context
  frame(gl) {
    const E = this.env;
    if (!E.heavyOk() || !this.live || !this.passes.size) return;
    this._tick++;
    // THE PASS INIT: one owed pass a frame while hidden (no frame of it is shown)
    if (this._uninit.length && E.hidden && E.hidden()) {
      const R = this._uninit.shift();
      E.sec(`gen.${R.pass.id}`);
      try { R.pass.init(gl); } catch (err) { console.warn(`gen.${R.pass.id} init:`, err && err.message); }
    }
    // a parked job no longer wanted (unlisted, forgotten, dead) gives its reserved slot back, as a current one would
    for (const [R, K] of this._parked) { const e = K.c.e; if (!(e.rank < Infinity) || R.tiles.get(e.id) !== e || R.dead.has(e.id)) this._unpark(R); }
    this._fetch();
    this._prep();
    let ms = Math.min((this.live.gen && this.live.gen.ms && this.live.gen.ms[genPhase(E.phase(), !!(E.hidden && E.hidden()))]) ?? 0, E.budget());
    if (!(ms > 0)) return;
    const paying = this._debt > 0;
    if (paying) { this._debt = Math.max(0, this._debt - ms); this._bank = 0; if (!this._debt) this.debtOf = null; return; }
    // the charged scan ms come off this slice first
    if (this._chg > 0) {
      const c = Math.min(this._chg, ms);
      this._chg -= c; ms -= c;
      if (!(ms > 0)) return;
    }
    // the floor: the current job's pass's minMs, else the largest any pass declares
    const fl = this._floor();
    if (ms < fl) {
      this._bank += ms;
      if (this._bank < fl) { this.stats.banked++; return; }
      ms = this._bank;
    }
    this._bank = 0;
    const t0 = E.now();
    let runs = 0, overId = null;
    for (;;) {
      const spent = E.now() - t0;
      if (runs > 0 && (spent >= ms || ms - spent < fl)) break;   // what is left would not hold the floor
      if (!this._cur) {
        // the first pick a slot takes (a pool holding only better tiles of its pass: the next pass's)
        for (const b of this._picks(true)) if ((this._cur = this._resume(b.R, b.e) || this._start(b.R, b.e))) break;
        if (!this._cur) break;
      }
      const c = this._cur;
      // no longer wanted mid-run: the reserved slot frees
      if (!(c.e.rank < Infinity)) { this._drop(c); continue; }
      E.sec(`gen.${c.R.pass.id}`);
      const lk = this._stepKey(c);   // (the run's kind, before it runs: its over-run is learned under it)
      let r;
      const given = Math.max(0, ms - spent), rt0 = E.now();
      try { r = c.R.pass.run(gl, c.job, given); } catch (err) {
        if (!(this.permanent && err && err.permanent)) console.warn(`gen.${c.R.pass.id} ${c.e.id}:`, err && err.message);
        this._failed(c.R, c.e, err, 'fail');
        this._tal(c.R.pass.id).ms += E.now() - rt0;
        this._drop(c); runs++; continue;
      }
      runs++; this.stats.runs++;
      // a run past what it was handed, by pass (state().over_by: whose run the debt is)
      const rd = E.now() - rt0;
      this._spend(c.R.pass.id, rd);
      const T = this._tal(c.R.pass.id); T.runs++; T.ms += rd;
      if (rd > given) {
        const id = (overId = c.R.pass.id), o = this.overBy[id] || (this.overBy[id] = { n: 0, ms: 0, max: 0, run_max: 0, at: 0 });
        o.n++; o.ms += rd - given; o.at = Math.round(E.now() / 100) / 10;
        if (rd - given > o.max) o.max = rd - given;
        if (rd > o.run_max) o.run_max = rd;
        const L = Math.min(this.learnMax, rd);
        if (L > this._learned(lk)) this.learn.set(lk, { ms: L, at: E.now() });
      }
      const wait = r && typeof r.then === 'function';
      if (wait && this.parkWait) {
        // THE PARKED WAIT: the job leaves the current slot until its input is in; the frame goes on
        const K = { c, ready: false };
        this._parked.set(c.R, K);
        Promise.resolve(r).then(() => { K.ready = true; }, () => { K.ready = true; });   // (a rejection: the pass's next run throws it)
        this._cur = null;
        continue;
      }
      if (r === 'done') { this._finish(c); this._cur = null; }
      else if (r === 'yield' || wait) {
        // a 'yield' claims the rest of the frame's ms (its GPU work fills it: the ground's paced steps, a
        // canopy stage) — charged to its pass's share, so the fair share counts frames held, not CPU alone
        const claim = Math.max(0, ms - (E.now() - t0));
        if (this.yieldShare) this._spend(c.R.pass.id, claim);
        T.ms += claim;
        break;
      }
    }
    const over = E.now() - t0 - ms;
    if (runs && over > 0) { this._debt += over; this.stats.debt_ms += over; this.debtOf = overId || this.debtOf; }
  }

  // a pass's learned floor now (its measured run, halving every LEARN_HALF_MS since)
  _learned(id) {
    const L = this.learn.get(id);
    return L ? Math.min(this.learnMax, L.ms * 2 ** (-(this.env.now() - L.at) / LEARN_HALF_MS)) : 0;
  }
  // the learned floor's key of the current job's next run: the pass id, + '.' + its step label (pass.step):
  // a pass whose runs differ (the canopy: a CPU prep up to ~15 ms, then GPU stages of ~0.3 ms CPU) banks
  // each kind to its own size — one floor learned from the prep held every stage run ~3 frames (fb0cc40e)
  _stepKey(c) {
    const P = c.R.pass, s = this.stepFloor && P.step ? P.step(c.job) : '';
    return s ? `${P.id}.${s}` : P.id;
  }
  _floor() {
    const m = (P, k = P && P.id) => Math.max(P && P.minMs ? Math.min(MIN_RUN_MS, Math.max(0, +P.minMs() || 0)) : 0, P ? this._learned(k) : 0);
    if (this._cur) return m(this._cur.R.pass, this._stepKey(this._cur));
    let f = 0;
    for (const R of this.passes.values()) f = Math.max(f, m(R.pass));
    return f;
  }

  _drop(c) {
    this._free(c);
    this._cur = null;
  }
  // a job given up: its reserved slot frees (its old one stays: the tile's last content)
  _free(c) {
    const P = c.R.pass.pool && this.pools.get(c.R.pass.pool);
    const l = c.job.target.layer;
    if (P && l >= 0 && l !== c.prev && P.slots[l] === c.e) P.slots[l] = null;
  }
  // THE PARKED WAIT: pass R's parked job given up (its slot frees) / resumed (entry e its pick: the job, current again)
  _unpark(R) {
    const K = this._parked.get(R);
    if (!K) return;
    this._parked.delete(R);
    this._free(K.c);
  }
  _resume(R, e) {
    const K = this._parked.get(R);
    if (!K || K.c.e !== e) return null;
    this._parked.delete(R);
    return K.c;
  }
  _parkedE(e) { for (const K of this._parked.values()) if (K.c.e === e) return true; return false; }

  // The load report's `gen` block (main.js buildLoadReport, PROTOCOL.md `load`), cumulative since page start:
  // per pass (<= GEN_REPORT_MAX, the most ms first) runs / fail (a run or prep that threw; a deterministic one once) / ffail (an input fetch that
  // failed) / ms (wall ms of its runs, failed ones and a yield's claimed rest of the frame included) / share
  // (its part of every pass's ms) / over (runs past the ms they were handed: n, summed ms, the largest) —
  // plus the debt left now (debt, debt_of its pass) and accrued in all (debt_ms). Read-only.
  report() {
    const r1 = (v) => Math.round(v * 10) / 10;
    let tot = 0;
    for (const t of this.tally.values()) tot += t.ms;
    const rows = [...this.tally].sort((a, b) => b[1].ms - a[1].ms).slice(0, GEN_REPORT_MAX).map(([id, t]) => {
      const o = this.overBy[id];
      return { id, runs: t.runs, fail: t.fail, ffail: t.ffail, ms: r1(t.ms), share: tot > 0 ? Math.round(t.ms / tot * 100) / 100 : 0,
        over: o ? o.n : 0, over_ms: o ? r1(o.ms) : 0, over_max: o ? Math.round(o.max * 10) / 10 : 0 };
    });
    return { ms: r1(tot), runs: this.stats.runs, fail: this.stats.failed, debt: r1(this._debt), debt_of: this.debtOf,
      debt_ms: r1(this.stats.debt_ms), passes: rows };
  }

  // first-minute-measure (telemetry `gen.pend`, main.js genTel): per pass the tiles wanted now (rank < Infinity)
  // whose slot does not show their newest content (none / stale), and of them the ones showing nothing yet
  // (none: a hole). Read-only.
  pending() {
    const out = {};
    for (const [id, R] of this.passes) {
      let n = 0, none = 0;
      for (const e of R.tiles.values()) {
        // (a pass without a pool — ground, ring — keeps no slot: its done key alone)
        const shown = R.pass.pool ? e.slot >= 0 && e.done : e.done;
        if (!(e.rank < Infinity) || (shown && e.done === this.jobKey(R, e))) continue;
        n++; if (!shown) none++;
      }
      out[id] = { n, none };
    }
    return out;
  }

  state() {
    let tiles = 0, fresh = 0, stale = 0;
    for (const R of this.passes.values()) for (const e of R.tiles.values()) {
      tiles++;
      if (e.slot >= 0) { if (e.done === this.jobKey(R, e)) fresh++; else stale++; }
    }
    return { passes: [...this.passes.keys()], tiles, fresh, stale, pool_mb: Math.round(this.poolMB() * 10) / 10, raw: this._raw.size, parked: this._parked.size,
      debt: Math.round(this._debt * 10) / 10, debt_of: this.debtOf, bank: Math.round(this._bank * 100) / 100, charge: Math.round(this._chg * 100) / 100, ...this.stats,
      floor: Object.fromEntries([...this.learn.keys()].map((k) => [k, Math.round(this._learned(k) * 100) / 100])),
      share: Object.fromEntries([...this._used().keys()].map((k) => [k, Math.round(this._share(k) * 10) / 10])),
      over_by: Object.fromEntries(Object.entries(this.overBy).map(([k, o]) => [k, { n: o.n, ms: Math.round(o.ms * 10) / 10, max: Math.round(o.max * 100) / 100, run_max: Math.round(o.run_max * 100) / 100, at: o.at }])) };
  }
}

// a pass's decoded inputs: an LRU of `cap` entries (get / put touch, put evicts the oldest) that never
// evicts a PINNED key — pin(keys): the inputs of the pass's queued (wanted, not fresh) tiles, set at the
// layer's want cadence. Past cap only while the pins need it; unpinned, it trims back to cap
export class InputCache {
  constructor(cap) { this.cap = cap; this.m = new Map(); this.pins = new Set(); this.pinned = true; }
  get size() { return this.m.size; }
  get(k) { const v = this.m.get(k); if (v !== undefined) { this.m.delete(k); this.m.set(k, v); } return v; }
  put(k, v) { this.m.delete(k); this.m.set(k, v); this._trim(); }
  pin(keys) { this.pins = new Set(keys); this._trim(); }
  _trim() {
    for (const k of this.m.keys()) {
      if (this.m.size <= this.cap) return;
      if (!this.pinned || !this.pins.has(k)) this.m.delete(k);
    }
  }
}

// ---- the module's one queue + the live parameters ------------------------
export const gen = new GenQueue();
let _g = null, _live = null, _cls = null, _hid = false, _warned = false;
const _ls = [];
export function onGenChange(fn) { _ls.push(fn); }
export const genLive = () => (_refresh(), _live);
// the tunables' gensurf block as served (every class's; a new object at every apply): the pre-paint key's
// params (paintkey.js paintParams) read it, not this class's live view
export const genRaw = () => _g;

// tunables: the whole tunables object (welcome / net.onTunables)
export function applyGenTunables(t) {
  _g = (t && t.gensurf) || null;
  if (!_g && !_warned) { _warned = true; console.warn('tunables carry no gensurf block: generation idle'); }
  if (_g) setGovernor(_g.governor || null);
  _refresh(true);
}

// the live view follows the tunables, the device class (known after
// detectDevice), the governor's factors and sched hidden (genFactors' floor)
function _refresh(force) {
  const cls = deviceClass(), hid = hidden();
  if (!force && cls === _cls && hid === _hid) return;
  _cls = cls;
  _hid = hid;
  let live = null;
  // a bad block (unknown version, a class missing) refuses generation, never the game
  try { live = _g && cls ? genResolve(_g, cls, RENDER_CFG.gensurf, genFactors(govFactors(), hid)) : null; } catch (e) { console.error(e.message); }
  const kinds = genDiff(_live, live);
  _live = live;
  gen.setLive(live);
  if (kinds.size) for (const f of _ls) f(kinds, live);
}
onGovernor(() => _refresh(true));

// once a tick (main.js)
export function genFrame(renderer) {
  _refresh(false);
  if (!_live) return;
  gen.frame(renderer && renderer.getContext ? renderer.getContext() : null);
}

// the load report's gen block (GenQueue.report), measured up to now
export const genReport = () => gen.report();
// per pass the wanted tiles not fresh (GenQueue.pending: telemetry `gen.pend`)
export const genPending = () => gen.pending();

export function genState() {
  // gov: the governor's factors; gen_f: the gen factor applied (genFactors: 1 at least while hidden)
  return { cls: _cls, live: !!_live, gov: govFactors(), hidden: _hid, gen_f: genFactors(govFactors(), _hid).gen, ...gen.state() };
}

export { DEVICE_CLASSES };
